/**
 * Signaling client: one WebSocket to the JARVIS Worker, with reconnect.
 *
 * Carries SDP and ICE only. Camera media never passes through here — it goes
 * peer-to-peer, preferably over the USB-tether network.
 */

import type {
  ClientMessage,
  DeviceCredential,
  HelloMessage,
  IceCandidatePayload,
  PeerInfo,
  ServerMessage,
} from '../../shared/signaling/protocol.ts';
import { CLOSE, PC_PEER_ID, PROTOCOL_VERSION } from '../../shared/signaling/protocol.ts';

export type LinkState = 'idle' | 'connecting' | 'online' | 'retrying' | 'revoked' | 'rejected';

export interface SignalingEvents {
  state(state: LinkState, detail?: string): void;
  hello(msg: HelloMessage): void;
  presence(peers: PeerInfo[]): void;
  offer(sessionId: string, sdp: string): void;
  answer(sessionId: string, sdp: string): void;
  ice(sessionId: string, candidate: IceCandidatePayload | null): void;
  bye(sessionId: string, reason?: string): void;
  error(code: string, message: string): void;
  /** Round-trip time to the Worker, measured not guessed. */
  rtt(ms: number): void;
  cameraCommand?(sessionId: string, command: string, value: unknown): void;
  cameraCapabilities?(sessionId: string, torch: boolean, zoom: { min: number; max: number; step: number } | null, lenses: string[]): void;
}

const PING_INTERVAL_MS = 25_000;
/** Exponential backoff, capped so a phone in a lift recovers promptly. */
const BACKOFF_MS = [500, 1_000, 2_000, 5_000, 10_000, 20_000];

export class Signaling {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private pingTimer: number | null = null;
  private retryTimer: number | null = null;
  private pending = new Map<string, number>();
  /** Set when the user asked to stop, so a close is not treated as a fault. */
  private stopped = false;

  constructor(
    private readonly endpoint: string,
    private readonly credential: DeviceCredential,
    private readonly on: SignalingEvents,
  ) {}

  get online(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  connect(): void {
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    this.ws?.close(CLOSE.NORMAL, 'client stop');
    this.ws = null;
    this.on.state('idle');
  }

  /* --------------------------------------------------------------- socket -- */

  private open(): void {
    this.clearTimers();
    this.on.state(this.attempt === 0 ? 'connecting' : 'retrying');

    const u = new URL('/ws', this.endpoint.replace(/^http/, 'ws'));
    u.searchParams.set('install', this.credential.installId);
    u.searchParams.set('role', 'phone');
    u.searchParams.set('device', this.credential.deviceId);
    u.searchParams.set('secret', this.credential.deviceSecret);

    const ws = new WebSocket(u.toString());
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.startPing();
    };

    ws.onmessage = (event) => this.dispatch(event.data as string);

    ws.onclose = (event) => {
      this.clearTimers();
      if (this.stopped) return;

      /* A revoked or rejected device must not spin forever: the credential is
         dead and only a fresh pairing can fix it. */
      if (event.code === CLOSE.REVOKED) {
        this.on.state('revoked', 'Cet appareil a été révoqué depuis le PC.');
        return;
      }
      if (event.code === CLOSE.UNAUTHORIZED) {
        this.on.state('rejected', "Le PC n'a pas accepté cet appareil.");
        return;
      }
      /* 4008 means the PC opened a newer socket for us; reconnecting would
         start a fight, so treat it as a clean stop. */
      if (event.code === CLOSE.REPLACED) {
        this.on.state('idle', 'Session remplacée par une connexion plus récente.');
        return;
      }
      this.scheduleRetry();
    };

    ws.onerror = () => {
      /* `onclose` always follows, and it carries the code we need. */
    };
  }

  private scheduleRetry(): void {
    const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)]!;
    this.attempt += 1;
    this.on.state('retrying', `Nouvelle tentative dans ${Math.round(delay / 1000)} s`);
    this.retryTimer = window.setTimeout(() => this.open(), delay);
  }

  /** Called when the tab is shown again: a backgrounded socket is often dead. */
  wake(): void {
    if (this.stopped || this.online) return;
    this.attempt = 0;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.open();
  }

  private dispatch(raw: string): void {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(raw) as ServerMessage;
    } catch {
      return;
    }

    switch (msg.type) {
      case 'hello':
        this.on.state('online');
        this.on.hello(msg);
        this.on.presence(msg.peers);
        break;
      case 'presence':
        this.on.presence(msg.peers);
        break;
      case 'offer':
        this.on.offer(msg.sessionId, msg.sdp);
        break;
      case 'answer':
        this.on.answer(msg.sessionId, msg.sdp);
        break;
      case 'ice':
        this.on.ice(msg.sessionId, msg.candidate);
        break;
      case 'bye':
        this.on.bye(msg.sessionId, msg.reason);
        break;
      case 'camera-command': {
        const c = msg as import('../../shared/signaling/protocol.ts').CameraCommandMessage;
        this.on.cameraCommand?.(c.sessionId, c.command, c.value);
        break;
      }
      case 'camera-capabilities': {
        const c = msg as import('../../shared/signaling/protocol.ts').CameraCapabilitiesMessage;
        this.on.cameraCapabilities?.(c.sessionId, c.torch, c.zoom, c.lenses);
        break;
      }
      case 'pong': {
        const sent = this.pending.get(msg.nonce);
        if (sent !== undefined) {
          this.pending.delete(msg.nonce);
          this.on.rtt(Math.round(performance.now() - sent));
        }
        break;
      }
      case 'error':
        this.on.error(msg.code, msg.message);
        break;
      default:
        /* Unknown types are ignored: a newer Worker may add messages this
           build does not need. */
        break;
    }
  }

  /* ---------------------------------------------------------------- send -- */

  private send(msg: ClientMessage): boolean {
    if (!this.online) return false;
    this.ws!.send(JSON.stringify({ ...msg, v: PROTOCOL_VERSION, ts: Date.now() }));
    return true;
  }

  sendOffer(sessionId: string, sdp: string): boolean {
    return this.send({ v: PROTOCOL_VERSION, type: 'offer', to: PC_PEER_ID, sessionId, sdp });
  }

  sendAnswer(sessionId: string, sdp: string): boolean {
    return this.send({ v: PROTOCOL_VERSION, type: 'answer', to: PC_PEER_ID, sessionId, sdp });
  }

  sendIce(sessionId: string, candidate: IceCandidatePayload | null): boolean {
    return this.send({ v: PROTOCOL_VERSION, type: 'ice', to: PC_PEER_ID, sessionId, candidate });
  }

  sendBye(sessionId: string, reason?: string): boolean {
    return this.send({ v: PROTOCOL_VERSION, type: 'bye', to: PC_PEER_ID, sessionId, reason });
  }

  sendCameraState(state: {
    active: boolean;
    facing: 'user' | 'environment' | null;
    width: number | null;
    height: number | null;
    frameRate: number | null;
    supports: { zoom: boolean; torch: boolean; focus: boolean };
  }): boolean {
    return this.send({ v: PROTOCOL_VERSION, type: 'camera-state', to: PC_PEER_ID, state });
  }

  sendCameraCapabilities(sessionId: string, torch: boolean, zoom: { min: number; max: number; step: number } | null, lenses: string[]): boolean {
    return this.send({ v: PROTOCOL_VERSION, type: 'camera-capabilities', to: PC_PEER_ID, sessionId, torch, zoom, lenses });
  }

  /* --------------------------------------------------------------- ping --- */

  private startPing(): void {
    this.ping();
    this.pingTimer = window.setInterval(() => this.ping(), PING_INTERVAL_MS);
  }

  private ping(): void {
    const nonce = crypto.randomUUID();
    this.pending.set(nonce, performance.now());
    /* Drop stale entries so a flaky link cannot grow this map without bound. */
    if (this.pending.size > 8) {
      const oldest = this.pending.keys().next().value;
      if (oldest !== undefined) this.pending.delete(oldest);
    }
    this.send({ v: PROTOCOL_VERSION, type: 'ping', nonce });
  }

  private clearTimers(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}
